import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Phone, Clock, ArrowRight, Navigation, Star } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import BranchMap from "@/components/BranchMap";

const DAYS = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag"];

export default function StandortDetail() {
  const { slug } = useParams();
  const [b, setB] = useState(null);
  const [menu, setMenu] = useState(null);
  const [offers, setOffers] = useState([]);
  const [rewards, setRewards] = useState([]);

  useEffect(() => {
    api.get(`/branches/${slug}`).then((r) => {
      setB(r.data);
      if (r.data?.id) {
        api.get(`/menus?branch_id=${r.data.id}`).then((m) => setMenu(m.data[0] || null));
        api.get(`/offers?branch_id=${r.data.id}`).then((o) => setOffers(o.data));
        api.get(`/rewards?branch_id=${r.data.id}`).then((rw) => setRewards(rw.data));
      }
    });
  }, [slug]);

  if (!b || !b.id) return <div className="max-w-7xl mx-auto px-4 py-24 text-gray-400">Standort wird geladen…</div>;

  const jsonld = {
    "@context": "https://schema.org", "@type": "Restaurant", name: b.name,
    address: { "@type": "PostalAddress", streetAddress: b.address?.street, postalCode: b.address?.zip, addressLocality: b.address?.city, addressCountry: "DE" },
    telephone: b.phone, geo: { "@type": "GeoCoordinates", latitude: b.lat, longitude: b.lng },
  };

  return (
    <div>
      <Seo title={b.seo_title} description={b.seo_description} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonld) }} />

      <section className="relative h-64 sm:h-80">
        <img src={b.photos?.[0]} alt={b.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 hero-scrim" />
        <div className="absolute bottom-0 left-0 right-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 text-white">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#E0243A]">{b.hospital_name}</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold font-display mt-1">{b.name}</h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-10">
          <div>
            <h2 className="text-2xl font-bold mb-4">Wochenmenü</h2>
            {menu ? (
              <div className="grid sm:grid-cols-2 gap-4">
                {DAYS.map((d) => (
                  <div key={d} className="bg-white rounded-xl border border-gray-100 p-4">
                    <div className="font-semibold text-[#E0243A] mb-2">{d}</div>
                    {(menu.days?.[d] || []).map((it, i) => (
                      <div key={i} className="flex justify-between text-sm py-1 border-b border-gray-50 last:border-0">
                        <span className="text-gray-700">{it.name}</span><span className="text-gray-500">{it.price?.toFixed(2)} €</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ) : <p className="text-gray-500">Kein Menü hinterlegt.</p>}
            <Link to="/wochenmenue" className="mt-4 inline-flex items-center gap-2 font-semibold text-[#E0243A]">Ganzes Wochenmenü <ArrowRight className="w-4 h-4" /></Link>
          </div>

          {offers.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-4">Aktuelle Angebote</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {offers.map((o) => (
                  <div key={o.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden flex">
                    <img src={o.image} alt={o.title} className="w-24 h-24 object-cover" />
                    <div className="p-3">
                      <div className="font-semibold">{o.title}</div>
                      <div className="text-sm text-gray-500">{o.description}</div>
                      <div className="text-[#E0243A] font-bold mt-1">{o.price?.toFixed(2)} €{o.ma_price != null && o.ma_price !== "" ? <span className="block text-xs font-semibold text-gray-500">MA {Number(o.ma_price).toFixed(2)} €</span> : null}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {rewards.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-4">Verfügbare Prämien</h2>
              <div className="flex flex-wrap gap-3">
                {rewards.map((r) => (
                  <div key={r.id} className="flex items-center gap-2 bg-[#FDF2F4] text-[#C81E33] px-4 py-2 rounded-xl text-sm font-medium">
                    <Star className="w-4 h-4" /> {r.name} · {r.points_required} Pkt.
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <div className="space-y-3 text-sm text-gray-700">
              <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#E0243A]" /> {b.address?.street}, {b.address?.zip} {b.address?.city}</div>
              <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#E0243A]" /> {b.phone}</div>
              <div className="pt-2 border-t border-gray-100">
                <div className="font-semibold flex items-center gap-2 mb-1"><Clock className="w-4 h-4 text-[#E0243A]" /> Öffnungszeiten</div>
                {Object.entries(b.opening_hours || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between text-gray-600"><span>{k}</span><span>{v}</span></div>
                ))}
              </div>
            </div>
            <a href={`https://www.openstreetmap.org/directions?to=${b.lat},${b.lng}`} target="_blank" rel="noreferrer"
              data-testid="branch-route-btn" className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#E0243A] hover:bg-[#C81E33] text-white font-semibold py-3 rounded-xl">
              <Navigation className="w-4 h-4" /> Route planen
            </a>
          </div>
          <div className="rounded-2xl overflow-hidden border border-gray-200"><BranchMap branches={[b]} height={240} center={[b.lat, b.lng]} zoom={14} /></div>
          <div className="bg-[#F9F8F6] rounded-2xl p-5 text-sm">
            <div className="font-semibold mb-1">Catering für Veranstaltungen</div>
            <p className="text-gray-600">Buffets, Konferenzen & Events an diesem Standort.</p>
            <Link to="/catering" className="mt-2 inline-flex items-center gap-1 text-[#E0243A] font-semibold">Catering anfragen <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
